import { IRepeatTaskInstance, IRepeatTaskPayload } from "models/tasks";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import { useEffect, useState } from "react";

interface IGetRepeatTaskResult {
    totalItems: number;
    tasks: IRepeatTaskInstance[];
}

type SetRepeatTaskSignature = (payload: IRepeatTaskPayload[]) => void;

type UseTaskRepeatTasksResult = [
    IRepeatTaskInstance[] | null,
    number,
    boolean,
    SetRepeatTaskSignature,
    boolean,
    string | null 
];

type UseTaskRepeatTasksSignature = (taskId: number, requested: boolean, 
    onSaveComplete?: (success: boolean, message?: string) => void
) => UseTaskRepeatTasksResult;

const useTaskRepeatTasks: UseTaskRepeatTasksSignature = (taskId, requested, onSaveComplete) => {

    const [ data, setData ] = useState<IRepeatTaskInstance[] | null>(null);
    const [ total, setTotal ] = useState<number>(0);
    const [ loading, setLoading ] = useState<boolean>(false);
    const [ working, setWorking ] = useState<boolean>(false);
    const [ message, setMessage ] = useState<string | null>(null);

    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    useEffect(() => {
        if (user === null) return;
        if (requested === false) return;
        if (taskId === undefined || taskId === 0) return;

        const controller = new AbortController();

        setLoading(true);
        (async () => {
            try {
                const resp = await fetcher(`/api/v3/tasks/${taskId}/repeat-tasks`, {
                    signal: controller.signal
                });

                if (!resp.ok) {
                    // errors
                }

                const result = await resp.json() as IGetRepeatTaskResult;

                setData(result.tasks);
                setTotal(result.totalItems);
            } catch (err) {
                throw err;
            } finally {
                setLoading(false);
            }
        })();

        return () => {
            controller.abort();
        };
    }, [ taskId, requested, user ]);

    const addRepeatTasks = (payload: IRepeatTaskPayload[]) => {
        (async () => {
            setWorking(true);
            try {
                const resp = await fetcher(`/api/v3/tasks/${taskId}/repeat-tasks`, {
                    method: 'post',
                    body: JSON.stringify(payload)
                });

                if (!resp.ok) {
                    // ERRORS                    
                    if (resp.status === 400) {
                        const body = await resp.json();
                        const message = body.errors['*'];

                        onSaveComplete?.(false, message);
                        setMessage(message ?? null);
                        return;
                    }
                }

                onSaveComplete?.(resp.ok);
                setMessage(null);
            } catch (err) {
                console.log(err);
            } finally {
                setWorking(false);
            }
        })();
    };

    return [
        data,
        total,
        loading,
        addRepeatTasks,
        working,
        message
    ];
};

export default useTaskRepeatTasks;